import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Receipt } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

export default function InvoiceCreateDialog({ job, open, onClose, onCreated, directMode = false }) {
  const [lineItems, setLineItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [markPaid, setMarkPaid] = useState(true);
  const [taxRate, setTaxRate] = useState(0.10);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (!open) return;
    if (directMode) {
      setLineItems([{ description: "", qty: 1, unit_price: 0 }]);
      setCustomerName(""); setCustomerEmail(""); setCustomerPhone(""); setAddress("");
    } else {
      if (!job) return;
      setLineItems([
        { description: `Camera installation — ${job.num_cameras || 0} cameras · ${job.installation_type || "wired"}`, qty: 1, unit_price: job.price || 0 },
      ]);
      setCustomerName(job.customer_name || "");
      setCustomerEmail(job.customer_email || "");
      setCustomerPhone(job.customer_phone || "");
      setAddress(job.address || "");
    }
    setPaymentMethod("bank_transfer");
    setMarkPaid(true);
    setTaxRate(0.10);
    setNotes("");
  }, [job, open, directMode]);

  const subtotal = lineItems.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);
  const tax = subtotal * (taxRate || 0);
  const total = subtotal + tax;

  const add = () => setLineItems([...lineItems, { description: "", qty: 1, unit_price: 0 }]);
  const remove = (i) => setLineItems(lineItems.filter((_, j) => j !== i));
  const setLi = (i, k, v) => { const c = [...lineItems]; c[i] = { ...c[i], [k]: v }; setLineItems(c); };

  const create = async () => {
    if (directMode && !customerName.trim()) { toast.error("Customer name is required"); return; }
    setBusy(true);
    try {
      const items = lineItems.filter(li => li.description).map(li => ({ description: li.description, qty: Number(li.qty || 0), unit_price: Number(li.unit_price || 0) }));
      if (items.length === 0) { toast.error("Add at least one line item"); return; }
      const endpoint = directMode ? "/admin/invoices/create-direct" : "/admin/invoices/create";
      const payload = directMode
        ? { line_items: items, payment_method: paymentMethod, mark_paid: markPaid, tax_rate: taxRate, notes, customer_name: customerName, customer_email: customerEmail, customer_phone: customerPhone, address }
        : { job_id: job.job_id, line_items: items, payment_method: paymentMethod, mark_paid: markPaid, tax_rate: taxRate, notes, customer_name: customerName, customer_email: customerEmail, customer_phone: customerPhone, address };
      const { data } = await api.post(endpoint, payload);
      toast.success(`Invoice ${data.invoice_number} created`);
      onCreated?.(data); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Receipt className="h-5 w-5 text-eagle-gold" />{directMode ? "New Invoice" : `Generate invoice — ${job?.customer_name}`}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <Label className="mb-2 block">Bill to</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg bg-secondary/50">
              <div><Label className="text-xs text-muted-foreground">Name{directMode ? " *" : ""}</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="invoice-customer-name" /></div>
              <div><Label className="text-xs text-muted-foreground">Email</Label><Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} data-testid="invoice-customer-email" /></div>
              <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} data-testid="invoice-customer-phone" /></div>
              <div><Label className="text-xs text-muted-foreground">Address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="invoice-customer-address" /></div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line items</Label>
              <Button variant="outline" size="sm" onClick={add} data-testid="invoice-add-line"><Plus className="h-3 w-3 mr-1" />Add line</Button>
            </div>
            <div className="space-y-2">
              {lineItems.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-6" placeholder="Description" value={li.description} onChange={(e) => setLi(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} />
                  <Input className="col-span-2" type="number" min={0} step="0.5" placeholder="Qty" value={li.qty} onChange={(e) => setLi(i, "qty", e.target.value)} data-testid={`invoice-line-qty-${i}`} />
                  <Input className="col-span-3" type="number" min={0} step="0.01" placeholder="Unit price" value={li.unit_price} onChange={(e) => setLi(i, "unit_price", e.target.value)} data-testid={`invoice-line-price-${i}`} />
                  <Button variant="ghost" size="sm" onClick={() => remove(i)} className="col-span-1"><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Payment method (received in)</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="invoice-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card (Eftpos)</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GST rate</Label>
              <Input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(parseFloat(e.target.value || 0))} data-testid="invoice-tax-rate" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 pb-2 cursor-pointer">
                <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} data-testid="invoice-mark-paid" className="h-4 w-4" />
                <span className="text-sm">Mark as paid now</span>
              </label>
            </div>
          </div>
          <div><Label>Notes on invoice</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="invoice-notes" placeholder="Thank you for your business!" /></div>

          <div className="p-4 rounded-lg bg-secondary text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>GST ({Math.round(taxRate * 100)}%)</span><span>${tax.toFixed(2)}</span></div>
            <div className="flex justify-between pt-2 mt-2 border-t border-border font-display text-lg font-bold"><span>Total</span><span className="text-eagle-gold">${total.toFixed(2)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={busy || lineItems.length === 0} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="invoice-create-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Receipt className="h-4 w-4 mr-2" />}Generate invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}